import { useQueryClient } from '@tanstack/react-query'
import { Button, Sheet, Typography } from '@cannect/new-components/atoms'
import {
  KEY_SUPPORT_CONVERSATION,
  useConversationStatus,
  useOpenConversation,
  useRequestHuman,
  useCloseConversation,
  useSendMessage,
  useUploadAttachment
} from '@cannect/services/resources/supportChat'
import {
  TOpenConversationResponse,
  TSupportAttachment,
  TSupportConversationStatus
} from '@cannect/services/resources/supportChat/types'
import { ReactNode, useEffect, useRef, useState } from 'react'
import {
  FiAlertCircle,
  FiArrowLeft,
  FiChevronRight,
  FiClock,
  FiCreditCard,
  FiFileText,
  FiHeadphones,
  FiHelpCircle,
  FiRefreshCw,
  FiTruck,
  FiWifiOff,
  FiXCircle
} from 'react-icons/fi'
import { startsNewBlock } from './grouping'
import MessageBubble from './MessageBubble'
import MessageComposer from './MessageComposer'
import SatisfactionSurvey from './SatisfactionSurvey'
import useConversationMessages from './useConversationMessages'

/**
 * Atalhos do primeiro contato.
 *
 * São os call drivers de pós-venda mais frequentes: em vez de o paciente ter
 * que formular a dúvida, ele clica e a frase pronta vai como mensagem dele —
 * o agente recebe um texto previsível e acerta a intenção na primeira.
 */
const QUICK_REPLIES: { icon: ReactNode; label: string; message: string }[] = [
  { icon: <FiTruck />, label: 'Onde está meu pedido?', message: 'Onde está meu pedido?' },
  { icon: <FiClock />, label: 'Prazo de entrega', message: 'Qual o prazo de entrega do meu pedido?' },
  { icon: <FiCreditCard />, label: 'Dúvida sobre pagamento', message: 'Tenho uma dúvida sobre o pagamento' },
  { icon: <FiFileText />, label: 'Nota fiscal', message: 'Preciso da nota fiscal do meu pedido' },
  { icon: <FiRefreshCw />, label: 'Troca ou devolução', message: 'Quero fazer uma troca ou devolução' },
  { icon: <FiHelpCircle />, label: 'Outro assunto', message: 'Preciso de ajuda com outro assunto' }
]

const formatWait = (seconds?: number | null) => {
  if (seconds === null || seconds === undefined) return null
  if (seconds < 60) return 'menos de um minuto'

  const minutes = Math.round(seconds / 60)

  if (minutes === 1) return 'cerca de 1 minuto'
  // Ninguém lê "cerca de 90 minutos" e entende uma hora e meia.
  if (minutes < 60) return `cerca de ${minutes} minutos`

  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  const horas = hours === 1 ? '1 hora' : `${hours} horas`

  return rest === 0 ? `cerca de ${horas}` : `cerca de ${horas} e ${rest} min`
}

const STATUS_TONE: Record<TSupportConversationStatus, { label: string; dot: string }> = {
  bot: { label: 'Assistente virtual', dot: 'bg-primary-600' },
  queued: { label: 'Aguardando atendente', dot: 'animate-pulse bg-tertiary-500' },
  assigned: { label: 'Falando com especialista', dot: 'animate-pulse bg-green-500' },
  closed: { label: 'Atendimento encerrado', dot: 'bg-neutral-300' }
}

type OrderHelpPanelProps = {
  /** Pedido sobre o qual o paciente pediu ajuda. `null` mantém o painel fechado. */
  orderId: number | null
  onClose: () => void
}

const OrderHelpPanel = ({ orderId, onClose }: OrderHelpPanelProps) => {
  const [isSending, setIsSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  /** Menu de assuntos: fecha ao escolher um, e o paciente pode reabrir. */
  const [isMenuOpen, setIsMenuOpen] = useState(true)
  const [isConfirmingClose, setIsConfirmingClose] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  // A conversa só é criada quando o painel abre com um pedido — abrir a lista
  // de pedidos não gera atendimento nenhum.
  const { data: opened, isLoading, error: openError } = useOpenConversation(orderId)

  const uuid = opened?.conversation.uuid ?? null
  const firebaseToken = opened?.firebase_token

  const {
    messages,
    meta,
    isReady,
    error: chatError
  } = useConversationMessages({
    conversationUuid: uuid,
    firebaseToken
  })

  // O `meta` do Realtime Database muda na hora em que o atendente assume; o
  // polling do MySQL é o que traz posição na fila e estimativa.
  const currentStatus: TSupportConversationStatus =
    (meta?.status as TSupportConversationStatus) ?? opened?.conversation.status ?? 'bot'

  const { data: status } = useConversationStatus(uuid, currentStatus === 'queued')

  const conversation = status ?? opened?.conversation ?? null
  const queue = conversation?.queue ?? null

  const requestHuman = useRequestHuman()
  const sendMessage = useSendMessage()
  const uploadAttachment = useUploadAttachment()
  const closeConversation = useCloseConversation()

  /**
   * O botão de WhatsApp do Botmaker é injetado no body por um script do
   * `index.html`, com z-index acima de todo o React — não dá para escondê-lo
   * por componente. A classe aqui aciona a regra que vive lá.
   */
  useEffect(() => {
    if (!orderId) return undefined

    document.body.classList.add('support-chat-open')

    return () => document.body.classList.remove('support-chat-open')
  }, [orderId])

  // Cada pedido começa com o menu aberto.
  useEffect(() => {
    setIsMenuOpen(true)
    setIsConfirmingClose(false)
  }, [orderId])

  /**
   * Conversa encerrada sai do cache ao reabrir o painel.
   *
   * A query de abertura é um POST que cria a conversa, então ela não pode
   * refetchar sozinha — mas, congelada, devolvia para sempre o atendimento
   * encerrado e o paciente não conseguia pedir ajuda de novo no mesmo pedido.
   * Limpar só na reabertura resolve os dois: nada é criado sem clique, e um
   * clique novo começa um atendimento novo.
   */
  useEffect(() => {
    if (!orderId) return

    const cached = queryClient.getQueryData<TOpenConversationResponse>([KEY_SUPPORT_CONVERSATION, orderId])

    if (cached?.conversation?.status === 'closed') {
      queryClient.removeQueries({ queryKey: [KEY_SUPPORT_CONVERSATION, orderId] })
    }
  }, [orderId, queryClient])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length, currentStatus])

  const hasPatientMessage = messages.some((message) => message.sender_type === 'patient')
  const showQuickReplies = currentStatus === 'bot' && isMenuOpen
  const isClosed = currentStatus === 'closed'

  /**
   * Escrever NÃO depende do Firebase.
   *
   * A mensagem sai por `POST /conversations/:uuid/messages` e é o backend que
   * grava no Realtime Database. O Firebase no navegador serve só para ESCUTAR.
   * Travar o campo quando a escuta falha deixaria o paciente mudo por causa de
   * um problema que não impede o envio.
   */
  const canWrite = !!uuid && !isClosed

  const handleSend = async (content: string, files: File[]) => {
    if (!uuid || (!content && files.length === 0)) return

    setIsSending(true)
    setSendError(null)
    setIsMenuOpen(false)

    try {
      // Os anexos sobem primeiro: a mensagem carrega as URLs, então ela só pode
      // ser publicada quando todos os arquivos já estiverem no S3.
      const attachments: TSupportAttachment[] = await Promise.all(
        files.map((file) => uploadAttachment.mutateAsync({ uuid, file }))
      )

      await sendMessage.mutateAsync({
        uuid,
        content,
        attachments
      })
    } catch (requestError: any) {
      setSendError(requestError?.response?.data?.message ?? 'Não conseguimos enviar sua mensagem. Tente de novo.')
    } finally {
      setIsSending(false)
    }
  }

  const renderStatusBanner = () => {
    if (currentStatus === 'assigned') {
      return (
        <div className="flex items-center gap-2 bg-primary-50 px-4 py-2.5 text-xs text-primary-900">
          <FiHeadphones className="shrink-0" />
          <span>
            {meta?.agent_name ? `${meta.agent_name} está no atendimento.` : 'Um especialista assumiu seu atendimento.'}
          </span>
        </div>
      )
    }

    if (currentStatus !== 'queued') return null

    if (queue && !queue.within_business_hours) {
      return (
        <div className="flex items-start gap-2 bg-tertiary-100 px-4 py-2.5 text-xs text-tertiary-900">
          <FiClock className="mt-0.5 shrink-0" />
          <span>
            Atendemos de segunda a sexta, das 9h às 18h. Você já está na fila e será chamado assim que abrirmos.
          </span>
        </div>
      )
    }

    const wait = formatWait(queue?.estimated_wait_seconds)

    return (
      <div className="flex items-start gap-2 bg-tertiary-100 px-4 py-2.5 text-xs text-tertiary-900">
        <FiClock className="mt-0.5 shrink-0 animate-pulse" />
        <span>
          Você está na fila
          {queue?.position ? ` na posição ${queue.position}` : ''}.{wait ? ` Espera ${wait}.` : ''} Pode continuar
          escrevendo — o especialista lê tudo ao entrar.
        </span>
      </div>
    )
  }

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-2">
          <span className="h-8 w-8 animate-spin rounded-full border-2 border-primary-200 border-t-primary-700" />
          <p className="text-xs text-neutral-500">Abrindo seu atendimento…</p>
        </div>
      )
    }

    // Só a falha de ABRIR a conversa justifica esvaziar a tela: sem conversa não
    // há para onde enviar nada.
    if (openError) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
          <FiAlertCircle size={26} className="text-red-500" />
          <p className="text-sm font-semibold text-neutral-800">Não conseguimos abrir o chat agora.</p>
          <p className="text-xs text-neutral-500">Tente novamente em alguns instantes.</p>
        </div>
      )
    }

    return (
      <>
        <div className="flex gap-2">
          <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-800 text-xs font-bold text-white">
            B
          </span>
          <div className="rounded-2xl rounded-tl-sm border border-neutral-200 bg-white px-4 py-3 shadow-sm">
            <p className="text-sm leading-relaxed text-neutral-800">
              Oi! Sou a Bia, assistente da Cannect. Estou aqui para ajudar com o{' '}
              <strong className="font-semibold">pedido #{orderId}</strong>.
            </p>
          </div>
        </div>

        {messages.map((message, index) => (
          <MessageBubble
            key={message.id}
            message={message}
            isOwn={message.sender_type === 'patient'}
            showSender={startsNewBlock(message, messages[index - 1])}
          />
        ))}

        {showQuickReplies && (
          <div className="flex flex-col gap-2 pt-1">
            <span className="px-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
              Sobre o que você quer falar?
            </span>

            {QUICK_REPLIES.map((option) => (
              <button
                key={option.label}
                type="button"
                disabled={!canWrite || isSending}
                onClick={() => handleSend(option.message, [])}
                className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-left text-sm text-neutral-800 shadow-sm transition hover:border-primary-300 hover:bg-primary-50 disabled:opacity-50">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-800">
                  {option.icon}
                </span>
                <span className="flex-1 font-medium">{option.label}</span>
                <FiChevronRight className="shrink-0 text-neutral-400" />
              </button>
            ))}
          </div>
        )}

        {isClosed && (
          <div className="flex flex-col gap-3 pt-2">
            <p className="rounded-xl bg-neutral-100 px-4 py-3 text-center text-xs text-neutral-600">
              Este atendimento foi encerrado. Se precisar, peça ajuda de novo pelo pedido.
            </p>

            {/*
              A pesquisa só aparece para quem foi atendido de fato. Perguntar
              "como foi o atendimento" a quem abriu e fechou sem conversar
              contamina a média com respostas sobre nada.
            */}
            {uuid && hasPatientMessage && (
              <SatisfactionSurvey conversationUuid={uuid} answeredScore={conversation?.nps_score ?? null} />
            )}
          </div>
        )}

        <div ref={bottomRef} />
      </>
    )
  }

  // Enquanto a escuta não subiu, o cabeçalho diz isso em vez de anunciar um
  // estado que ainda não está sendo acompanhado em tempo real.
  const tone =
    uuid && !isReady && !chatError
      ? { label: 'Conectando…', dot: 'animate-pulse bg-neutral-300' }
      : STATUS_TONE[currentStatus]

  return (
    <Sheet.Root open={!!orderId} onOpenChange={(open) => !open && onClose()}>
      {/*
        Overlay próprio, mais leve que o padrão (`bg-black/80`): o chat abre por
        cima da lista de pedidos e o paciente continua consultando o pedido ali
        atrás enquanto escreve. Escurecer tudo transforma um painel de apoio em
        uma janela modal que esconde justamente o que se está discutindo.
      */}
      <Sheet.Portal>
        <Sheet.Overlay className="bg-neutral-900/25 backdrop-blur-[2px]" />
      </Sheet.Portal>

      <Sheet.Content
        hideOverlay
        side="right"
        aria-label="Chat de ajuda do pedido"
        className="flex h-[100dvh] w-screen max-w-none flex-col gap-0 rounded-none bg-white p-0 shadow-2xl sm:w-[440px] sm:max-w-[92vw] sm:rounded-l-2xl md:max-w-[440px] lg:max-w-[460px]">
        {/*
          `pl-12` em todas as larguras: o botão de fechar do Sheet é ancorado em
          `left-2` neste lado e, sem essa folga, ele cai em cima do avatar — que
          um dia vira a foto da Bia e ficaria ilegível do mesmo jeito.
        */}
        <Sheet.Header className="mt-0 flex-row items-center gap-3 border-b border-neutral-200 bg-white py-3 pl-12 pr-3">
          <span
            aria-hidden
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-800 text-base font-bold text-white">
            B
          </span>

          <div className="flex min-w-0 flex-1 flex-col">
            <Sheet.Title className="truncate text-base leading-tight">Ajuda do seu pedido</Sheet.Title>
            <Sheet.Description className="flex items-center gap-1.5 text-xs">
              <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${tone.dot}`} />
              <span className="truncate">
                Pedido #{orderId} · {tone.label}
              </span>
            </Sheet.Description>
          </div>

          {/* Voltar para os assuntos: some quando o menu já é o que está na tela. */}
          {currentStatus === 'bot' && !isMenuOpen && !openError && (
            <button
              type="button"
              onClick={() => setIsMenuOpen(true)}
              title="Voltar para os assuntos"
              aria-label="Voltar para os assuntos"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-600 transition hover:bg-neutral-50">
              <FiArrowLeft size={16} />
            </button>
          )}
        </Sheet.Header>

        {renderStatusBanner()}

        {/* Falha de escuta não trava a conversa: avisa e deixa escrever. */}
        {chatError && !openError && (
          <div className="flex items-start gap-2 bg-amber-50 px-4 py-2 text-[11px] text-amber-800">
            <FiWifiOff className="mt-0.5 shrink-0" />
            <span>Pode escrever normalmente. Estamos reconectando para mostrar as respostas aqui.</span>
          </div>
        )}

        <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto bg-neutral-50 p-4">{renderBody()}</div>

        {sendError && <p className="bg-red-50 px-4 py-2 text-center text-xs text-red-700">{sendError}</p>}

        {/*
          O atalho para o humano só aparece depois que o paciente já pediu algo:
          oferecer especialista antes de ele tentar joga na fila um atendimento
          que a assistente resolveria sozinha.
        */}
        {currentStatus === 'bot' && !openError && hasPatientMessage && (
          <div className="border-t border-neutral-200 bg-white px-4 py-2.5">
            <Button
              variant="outline_light"
              size="md"
              full
              disabled={!canWrite || requestHuman.isPending}
              onClick={() => uuid && requestHuman.mutate(uuid)}>
              <FiHeadphones size={14} />
              {requestHuman.isPending ? 'Entrando na fila…' : 'Falar com um especialista'}
            </Button>
          </div>
        )}

        {/*
          Encerrar é diferente de fechar o painel: o X só esconde a janela e a
          conversa continua viva. Encerrar arquiva o atendimento e libera o
          atendente — por isso pede confirmação e não fica junto do X.
        */}
        {!isClosed && !openError && hasPatientMessage && (
          <div className="border-t border-neutral-200 bg-white px-4 py-2">
            {isConfirmingClose ? (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Typography.Text type="captionOne" muted>
                  Encerrar este atendimento?
                </Typography.Text>
                <div className="flex gap-2">
                  <Button variant="gray_light" size="xs" onClick={() => setIsConfirmingClose(false)}>
                    Cancelar
                  </Button>
                  <Button
                    variant="gray"
                    size="xs"
                    disabled={closeConversation.isPending}
                    onClick={() => uuid && closeConversation.mutate(uuid)}>
                    {closeConversation.isPending ? 'Encerrando…' : 'Sim, encerrar'}
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsConfirmingClose(true)}
                className="flex w-full items-center justify-center gap-1.5 bg-transparent text-[11px] text-neutral-500 transition hover:text-neutral-800">
                <FiXCircle size={12} />
                Encerrar atendimento
              </button>
            )}
          </div>
        )}

        <MessageComposer
          disabled={!canWrite}
          isSending={isSending}
          placeholder={isClosed ? 'Atendimento encerrado' : 'Escreva sua mensagem…'}
          onSend={handleSend}
        />
      </Sheet.Content>
    </Sheet.Root>
  )
}

export default OrderHelpPanel
